import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/getApiUrl';

const PublicFileUpload: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            setError(null);

            // Create preview
            if (selectedFile.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreview(reader.result as string);
                };
                reader.readAsDataURL(selectedFile);
            } else {
                setPreview(null);
            }
        }
    };

    const [timeLeft, setTimeLeft] = useState<string>('');
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        const checkToken = async () => {
            if (!token) return;
            try {
                const response = await fetch(`${getApiBaseUrl()}/appointments/token/${token}`);
                if (!response.ok) {
                    if (response.status === 400 || response.status === 404) {
                        setIsExpired(true);
                        setError('لینک منقضی یا نامعتبر است.');
                    }
                    return;
                }
                const data = await response.json();
                const expiresAt = new Date(data.expires_at).getTime();

                const interval = setInterval(() => {
                    const now = new Date().getTime();
                    const distance = expiresAt - now;

                    if (distance < 0) {
                        clearInterval(interval);
                        setIsExpired(true);
                        setError('زمان آپلود به پایان رسیده است.');
                        setTimeLeft('منقضی شده');
                    } else {
                        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
                        setTimeLeft(`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`);
                    }
                }, 1000);

                return () => clearInterval(interval);

            } catch (err) {
                console.error("Error checking token", err);
            }
        };

        checkToken();
    }, [token]);

    const handleUpload = async () => {
        if (!file || !token || isExpired) return;

        setUploading(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch(`${getApiBaseUrl()}/appointments/upload-with-token/${token}`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'آپلود ناموفق بود');
            }

            setSuccess(true);
            setFile(null);
            setPreview(null);
        } catch (err: any) {
            setError(err.message || 'خطا در آپلود فایل. لطفا مجددا تلاش کنید.');
        } finally {
            setUploading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                        <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">آپلود موفقیت‌آمیز بود</h2>
                    <p className="text-gray-600 mb-6">فایل شما با موفقیت به پرونده نوبت اضافه شد. با تشکر.</p>
                    <button
                        onClick={() => setSuccess(false)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                        آپلود فایل دیگر
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-blue-600 px-6 py-4 flex justify-between items-center">
                    <h1 className="text-xl font-bold text-white">آپلود مدارک پزشکی</h1>
                    {timeLeft && !isExpired && !success && (
                        <div className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-mono dir-ltr">
                            {timeLeft}
                        </div>
                    )}
                </div>

                <div className="p-8">
                    <div className="mb-6 text-center">
                        <p className="text-gray-600 text-sm">
                            لطفاً فایل نسخه یا مدارک پزشکی خود را انتخاب و آپلود کنید.
                        </p>
                    </div>

                    {error && (
                        <div className="mb-6 bg-red-50 text-red-700 p-3 rounded-lg text-sm text-center">
                            {error}
                        </div>
                    )}

                    <div className="mb-8">
                        <label
                            htmlFor="file-upload"
                            className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${file ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                {preview ? (
                                    <img src={preview} alt="Preview" className="h-32 object-contain mb-2" />
                                ) : (
                                    <>
                                        <svg className="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <p className="mb-2 text-sm text-gray-500">
                                            <span className="font-semibold">برای انتخاب فایل کلیک کنید</span>
                                        </p>
                                        <p className="text-xs text-gray-500">یا فایل را اینجا رها کنید</p>
                                    </>
                                )}
                            </div>
                            <input
                                id="file-upload"
                                type="file"
                                className="hidden"
                                accept="image/*,application/pdf"
                                onChange={handleFileChange}
                            />
                        </label>
                        {file && (
                            <p className="mt-2 text-sm text-center text-gray-600 break-all">
                                {file.name}
                            </p>
                        )}
                    </div>

                    <button
                        onClick={handleUpload}
                        disabled={!file || uploading || isExpired}
                        className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white transition-colors ${!file || uploading || isExpired
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                            }`}
                    >
                        {uploading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                در حال آپلود...
                            </>
                        ) : (
                            'آپلود فایل'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PublicFileUpload;
